/**
 * Routing/loading-gate decisions for the top-level `App` component.
 *
 * Extracted as pure functions so they are testable without mounting the full app
 * (the vitest env here has no jsdom).
 *
 * Auth routes do not depend on an interactive user profile, so they must not be
 * blocked by the global loading gate (unmounting SignInPage on profile refresh).
 */

/** Auth routes rendered without the app shell (no authenticated profile required). */
export function isAuthPath(pathname: string): boolean {
  return (
    pathname === '/signin' ||
    pathname === '/forgot-password' ||
    pathname === '/auth/set-password' ||
    pathname === '/auth/setpassword' ||
    pathname.startsWith('/auth/callback') ||
    pathname.startsWith('/set-password/')
  );
}

/**
 * Signed telehealth guest links. Authorization lives in the URL token, and the guest's
 * `client_credentials` session has a ClientApplication profile, so these routes must
 * bypass both the loading gate and the interactive-user redirect to `/signin`.
 */
export function isGuestJoinPath(pathname: string): boolean {
  return pathname.startsWith('/join/');
}

/**
 * Whether the global `<Loading/>` gate should block rendering of the current route.
 *
 * Only non-auth routes are blocked while Medplum is loading. Auth routes never
 * block — they do not depend on the authenticated profile.
 */
export function shouldBlockRenderWhileLoading(isLoading: boolean, pathname: string): boolean {
  return isLoading && !isAuthPath(pathname) && !isGuestJoinPath(pathname);
}
